import { CurrencyTokenType, useCurrencyContext } from '@apeiron/currency'
import { NextImage, noForwardProps } from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import CheckInMakeupPriceTableTooltip from '@src/components/reward/checkIn/CheckInMakeupPriceTableTooltip'
import ApeironConfirmButton from '@src/components/share/apeiron/ApeironConfirmButton'
import { useDailyCheckInContext } from '@src/contexts/reward/DailyCheckInContext'
import useMakeupCheckIn from '@src/hooks/contract/useMakeupCheckIn'
import useSignDailyCheckInMakeup from '@src/hooks/graphql/useSignDailyCheckInMakeup'
import * as RA from 'ramda-adjunct'
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const Container = styled(Box)`
  width: 100%;
`

const AnimaTokenIcon = styled(NextImage)`
  width: 26px;
  height: 26px;
  flex-shrink: 0;
`

const AnimaNeededContainer = styled(Box)`
  display: flex;
  background-color: #000000;
  padding: 8px;
  align-items: center;
  border-radius: 3px;
  min-width: 130px;
`

const AnimaTokenAmount = styled(Typography)`
  color: #ffffff;
  margin-left: 10px;
  font-size: 20px;
  white-space: nowrap;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const AnimaTokenName = styled('span')`
  color: #ffffff99;
  font-size: 12px;
`

const ErrorText = styled(Typography)`
  color: red;
  font-size: 14px;
  margin-top: 5px;
`

const AnimaNeededDisplayBox = ({ animaNeeded }: { animaNeeded: string }) => {
  const { getCurrency } = useCurrencyContext()

  const animaToken = getCurrency(CurrencyTokenType.Anima)

  return (
    <AnimaNeededContainer>
      <AnimaTokenIcon src={animaToken?.image} />
      <AnimaTokenAmount>
        {animaNeeded} <AnimaTokenName>{animaToken?.name}</AnimaTokenName>
      </AnimaTokenAmount>
    </AnimaNeededContainer>
  )
}

const FlexBox = styled(Box, noForwardProps)<{ __wrap?: boolean }>`
  display: flex;
  gap: 10px;

  ${props => props.theme.breakpoints.down('res768')} {
    justify-content: center;
    ${({ __wrap }) => __wrap && 'flex-wrap: wrap;'}
  }
`

const InputContainer = styled(Box)`
  display: flex;
  gap: 10px;
  background: #000000;
  width: auto;
  align-items: center;
  position: relative;
  border-radius: 3px;
  border: 1px solid transparent;
  flex-grow: 1;
  min-height: 46px;

  :focus-within,
  :hover {
    border: 1px solid #ffffff;
  }
`

const CenterInput = styled('input')`
  width: 100%;
  text-align: center;
  background-color: transparent;
  border: none;
  color: white;
  height: 100%;
  font-size: 20px;

  :focus-visible {
    border: none;
    outline: none;
  }
`

const UnitText = styled(Typography)`
  color: #ffffff99;
  font-size: 12px;
  position: absolute;
  right: 10px;
`

const InputBox = ({ onChange, value }: InputBoxProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null)

  const { t } = useTranslation()

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.length > 1) {
      return
    }

    onChange(event)
  }

  return (
    <InputContainer>
      <CenterInput
        ref={inputRef}
        type='text'
        value={value}
        onChange={handleChange}
        onFocus={() => {
          inputRef.current?.select()
        }}
      />
      <UnitText>{t('common.days')}</UnitText>
    </InputContainer>
  )
}

const HeaderContainer = styled(Box)`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`

const CheckInByAnimaSection: FC = () => {
  const { t } = useTranslation()

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    reset,
    watch
  } = useForm<FormValues>({
    mode: 'onChange'
  })

  const { getApeironPrice } = useCurrencyContext()

  const [animaNeeded, setAnimaNeeded] = useState<string>('0')

  const {
    myDailyCheckInCampaign,
    getMissedDaysCount,
    makeupPrices,
    getConsumeTokenCompletedMakeupDaysCount,
    fetchAfterOperation
  } = useDailyCheckInContext()

  const { mutate: signCheckInMakeup } = useSignDailyCheckInMakeup()

  const { makeupCheckIn } = useMakeupCheckIn()

  const campaign = myDailyCheckInCampaign[0]

  const missedDays = getMissedDaysCount(campaign)

  const days = watch('days')

  const totalMakeupPrice = useMemo(() => {
    const daysValue = parseInt(days)

    if (isNaN(daysValue) || daysValue <= 0 || daysValue > 6) {
      return BigInt(0)
    }

    const consumedTokenDayCount =
      getConsumeTokenCompletedMakeupDaysCount(campaign)

    return Array.from({ length: parseInt(days) }).reduce(
      (acc: bigint, _, index) => {
        const day = index + consumedTokenDayCount

        if (day >= 6) {
          return acc
        }

        return acc + BigInt(makeupPrices[day])
      },
      BigInt(0)
    )
  }, [campaign, days, getConsumeTokenCompletedMakeupDaysCount, makeupPrices])

  useEffect(() => {
    const animaString = getApeironPrice(
      CurrencyTokenType.Anima,
      totalMakeupPrice.toString(),
      {
        allowedLargeNumberAbbr: false
      }
    )

    setAnimaNeeded(animaString)
  }, [getApeironPrice, makeupPrices, totalMakeupPrice])

  const handleConfirm = useCallback(
    async (data: FormValues) => {
      if (!myDailyCheckInCampaign || myDailyCheckInCampaign.length === 0) {
        return
      }

      const signatureReturn = await signCheckInMakeup(
        myDailyCheckInCampaign[0].campaignID,
        parseInt(data.days)
      )

      if (!signatureReturn) {
        return false
      }

      const response = await makeupCheckIn({
        campaignID: signatureReturn.campaignID,
        maxMakeupCount: signatureReturn.maxMakeupCount,
        makeupDayCount: signatureReturn.makeupDayCount,
        animusUse: signatureReturn.animusUse,
        signTimeInSec: signatureReturn.signTimeInSec,
        signature: signatureReturn.signature,
        neededAnima: totalMakeupPrice.toString()
      })

      if (response) {
        await fetchAfterOperation()
        reset({
          days: ''
        })
      }

      return response
    },
    [makeupCheckIn, myDailyCheckInCampaign, signCheckInMakeup]
  )

  return (
    <Container>
      <HeaderContainer>
        <Typography>{t('reward.checkin.get_more_make_up')}</Typography>
        <CheckInMakeupPriceTableTooltip />
      </HeaderContainer>
      <form onSubmit={handleSubmit(handleConfirm)}>
        <FlexBox __wrap>
          <FlexBox>
            <Controller
              name='days'
              control={control}
              rules={{
                max: {
                  value: missedDays,
                  message: t('reward.checkin.makeup.error.too_large', {
                    missedDays
                  })
                },
                min: {
                  value: 1,
                  message: t('reward.checkin.makeup.error.too_small')
                },
                pattern: {
                  value: /^[1-6]$/,
                  message: t('reward.checkin.makeup.error.invalid')
                }
              }}
              render={({ field: { onChange, value } }) => (
                <InputBox onChange={onChange} value={value} />
              )}
            />
            <AnimaNeededDisplayBox animaNeeded={animaNeeded} />
          </FlexBox>
          <ApeironConfirmButton
            type={'submit'}
            disabled={!isDirty || !isValid || RA.isNilOrEmpty(days)}
          />
        </FlexBox>
        {errors && (
          <ErrorText>
            {Object.values(errors)
              .map(field => field.message)
              .join('\n')}
          </ErrorText>
        )}
      </form>
    </Container>
  )
}

type FormValues = {
  days: string
}

type InputBoxProps = {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  value: string
}

export default CheckInByAnimaSection
